import { z } from 'zod'

/**
 * Tablet ingest envelope. The `data` field is opaque to the server beyond
 * JSON-Schema validation against the referenced schema; `patient` carries the
 * indexable identifier fields lifted out of the data set so the server can
 * search without parsing payloads.
 */
export const SchemaRefSchema = z.string().regex(/^[a-z][a-z0-9_-]{0,63}@[1-9][0-9]{0,4}$/, {
  message: 'schema reference must be of form "name@version" (e.g. vitals@3)',
})

export const PatientHeaderSchema = z.object({
  name: z.string().min(1).max(200),
  dob: z.string().date().optional(),
  externalId: z.string().min(1).max(120).optional(),
})

export const IngestEnvelopeSchema = z.object({
  schema: SchemaRefSchema,
  tabletUid: z.string().min(8).max(128),
  capturedAt: z.string().datetime({ offset: true }),
  patient: PatientHeaderSchema,
  data: z.record(z.unknown()),
})

export type SchemaRef = z.infer<typeof SchemaRefSchema>
export type PatientHeader = z.infer<typeof PatientHeaderSchema>
export type IngestEnvelope = z.infer<typeof IngestEnvelopeSchema>

export function parseSchemaRef(ref: SchemaRef): { name: string; version: number } {
  const [name, versionStr] = ref.split('@')
  if (!name || !versionStr) {
    throw new Error(`invalid schema ref: ${ref}`)
  }
  const version = Number.parseInt(versionStr, 10)
  if (!Number.isFinite(version) || version <= 0) {
    throw new Error(`invalid schema version in ref: ${ref}`)
  }
  return { name, version }
}

export function formatSchemaRef(name: string, version: number): SchemaRef {
  return `${name}@${version}`
}
